import "@/App.css";
import axios from "axios";
import { useEffect } from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { track } from "@/lib/track";
import AnalyticsPage from "@/pages/AnalyticsPage";
import Dashboard from "@/pages/Dashboard";
import EvidencePage from "@/pages/EvidencePage";
import GapsPage from "@/pages/GapsPage";
import MAIScorePage from "@/pages/MAIScorePage";
import SubmissionsPage from "@/pages/SubmissionsPage";
import DecisionsPage from "@/pages/DecisionsPage";
import RoadmapPage from "@/pages/RoadmapPage";
import ExecutivePage from "@/pages/ExecutivePage";
import AccessCasesPage from "@/pages/AccessCasesPage";
import PlatformPage from "@/pages/PlatformPage";
import ScenarioPage from "@/pages/ScenarioPage";
import DossierPage from "@/pages/DossierPage";
import CompetitivePage from "@/pages/CompetitivePage";
import MethodologyPage from "@/pages/MethodologyPage";
import ReconciliationPage from "@/pages/ReconciliationPage";
import BulkUploadPage from "@/pages/BulkUploadPage";
import MergeReviewPage from "@/pages/MergeReviewPage";
import CitationsPage from "@/pages/CitationsPage";
import ClaimsPage from "@/pages/ClaimsPage";
import QualityPage from "@/pages/QualityPage";
import ReleasePage from "@/pages/ReleasePage";
import PlannerPage from "@/pages/PlannerPage";
import IntelMonitorPage from "@/pages/IntelMonitorPage";
import ValidationPage from "@/pages/ValidationPage";
import DictionaryPage from "@/pages/DictionaryPage";
import ReadinessPage from "@/pages/ReadinessPage";
import JourneyPage from "@/pages/JourneyPage";
import CopilotPage from "@/pages/CopilotPage";
import PrioritizationPage from "@/pages/PrioritizationPage";
import QueriesPage from "@/pages/QueriesPage";
import ReportPage from "@/pages/ReportPage";
import LibraryPage from "@/pages/LibraryPage";
import MonitorPage from "@/pages/MonitorPage";
import KnowledgePage from "@/pages/KnowledgePage";
import EpidemiologyPage from "@/pages/EpidemiologyPage";
import InventoryPage from "@/pages/InventoryPage";
import EvidenceLibraryPage from "@/pages/EvidenceLibraryPage";
import EvidenceReviewPage from "@/pages/EvidenceReviewPage";
import EvidenceInsightsPage from "@/pages/EvidenceInsightsPage";
import GVDPage from "@/pages/GVDPage";
import WorkspacePage from "@/pages/WorkspacePage";
import LoginPage from "@/pages/LoginPage";
import AuthCallback from "@/pages/AuthCallback";
import { Toaster } from "@/components/ui/sonner";
import { ProductProvider } from "@/context/ProductContext";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import SiteHome from "@/pages/site/SiteHome";
import WhoWeServe from "@/pages/site/WhoWeServe";
import OurApproach from "@/pages/site/OurApproach";
import Engagement from "@/pages/site/Engagement";
import Outcomes from "@/pages/site/Outcomes";
import Trust from "@/pages/site/Trust";
import RequestDemo from "@/pages/site/RequestDemo";
import DeckPage from "@/pages/site/DeckPage";

axios.defaults.withCredentials = true;

function ProtectedRoute({ children }) {
  const { user, loading } = useAuth();
  if (loading) return <div className="min-h-screen flex items-center justify-center bg-slate-50 text-sm text-slate-400">Loading…</div>;
  if (!user) return <Navigate to="/console/login" replace />;
  return children;
}

function AppRouter() {
  const location = useLocation();
  useEffect(() => { track("pageview", location.pathname); }, [location.pathname]);
  // Process OAuth session_id synchronously during render, before any auth check
  if (location.hash?.includes("session_id=")) return <AuthCallback />;
  return (
    <Routes>
      <Route path="/" element={<SiteHome />} />
      <Route path="/who-we-serve" element={<WhoWeServe />} />
      <Route path="/our-approach" element={<OurApproach />} />
      <Route path="/how-engagement-works" element={<Engagement />} />
      <Route path="/outcomes" element={<Outcomes />} />
      <Route path="/trust" element={<Trust />} />
      <Route path="/request-demo" element={<RequestDemo />} />
      <Route path="/deck" element={<DeckPage />} />
      <Route path="/login" element={<Navigate to="/console/login" replace />} />
      <Route path="/console/login" element={<LoginPage />} />
      <Route path="/console/*" element={
        <ProtectedRoute>
          <ProductProvider>
            <Routes>
              <Route path="/" element={<ReadinessPage />} />
              <Route path="/journey" element={<JourneyPage />} />
              <Route path="/copilot" element={<CopilotPage />} />
              <Route path="/prioritization" element={<PrioritizationPage />} />
              <Route path="/queries" element={<QueriesPage />} />
              <Route path="/library" element={<LibraryPage />} />
              <Route path="/report" element={<ReportPage />} />
              <Route path="/monitor" element={<MonitorPage />} />
              <Route path="/workspaces" element={<WorkspacePage />} />
              <Route path="/knowledge" element={<KnowledgePage />} />
              <Route path="/inventory" element={<InventoryPage />} />
              <Route path="/evidence-library" element={<EvidenceLibraryPage />} />
              <Route path="/evidence-review" element={<EvidenceReviewPage />} />
              <Route path="/evidence-insights" element={<EvidenceInsightsPage />} />
              <Route path="/gvd" element={<GVDPage />} />
              <Route path="/intelligence" element={<Dashboard />} />
              <Route path="/epidemiology" element={<EpidemiologyPage />} />
              <Route path="/reusability" element={<EvidencePage />} />
              <Route path="/gaps" element={<GapsPage />} />
              <Route path="/submissions" element={<SubmissionsPage />} />
              <Route path="/decisions" element={<DecisionsPage />} />
              <Route path="/roadmap" element={<RoadmapPage />} />
              <Route path="/executive" element={<ExecutivePage />} />
              <Route path="/access-cases" element={<AccessCasesPage />} />
              <Route path="/mai-score" element={<MAIScorePage />} />
              <Route path="/platform" element={<PlatformPage />} />
              <Route path="/whatif" element={<ScenarioPage />} />
              <Route path="/dossier" element={<DossierPage />} />
              <Route path="/competitive" element={<CompetitivePage />} />
              <Route path="/methodology" element={<MethodologyPage />} />
              <Route path="/reconciliation" element={<ReconciliationPage />} />
              <Route path="/bulk-upload" element={<BulkUploadPage />} />
              <Route path="/merge-review" element={<MergeReviewPage />} />
              <Route path="/citations" element={<CitationsPage />} />
              <Route path="/claims" element={<ClaimsPage />} />
              <Route path="/quality" element={<QualityPage />} />
              <Route path="/release" element={<ReleasePage />} />
              <Route path="/planner" element={<PlannerPage />} />
              <Route path="/intel-monitor" element={<IntelMonitorPage />} />
              <Route path="/validation" element={<ValidationPage />} />
              <Route path="/dictionary" element={<DictionaryPage />} />
              <Route path="/analytics" element={<AnalyticsPage />} />
            </Routes>
          </ProductProvider>
        </ProtectedRoute>
      } />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <AuthProvider>
          <AppRouter />
        </AuthProvider>
      </BrowserRouter>
      <Toaster position="bottom-right" />
    </div>
  );
}

export default App;
